/**
 * M9 CH-12a: the hulk claim loop in flight (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §4.5): scan → survey
 * card → vent / fence / fight → start the kit → deliver → claim. M7's reactivation live loop
 * (`story-link/reactivation/live.ts`) is the pattern; the rules are M9's pure modules.
 *
 * - **The hulks** of the current system come from two sources the feature hands in: the seeded First Rush spots
 *   (`hk:` keys, on system entry) and the traffic manifest's disabled or open-claim records (`ow-` keys). Each is a
 *   `HulkRef` (key, hull, seed, the system's region) at a float64 system-frame position.
 * - **CH-12e dev hulks** (`addDevHulk`): CH-12d's `hk:<sys>:dev<n>` hulks join the sources until the next system
 *   entry, seeded `devHulkSeed(key, region)`.
 * - **The survey** (`scanned`): a completed wreck scan of a hulk runs `hulkSurvey` with the ship's scanner Mk (Mk II+;
 *   a Mk I scan shows the refusal on a card with only *Close*); the survey is written once and CH-12b's card modal
 *   (`hulkCardModal(hulkScreenView(...))`) is shown. CH-12e: its answer goes through the same fleet calls as the
 *   prompts: *Start the claim* is the kit start, *Abandon the claim* is `FleetService.closeHulkClaim` (the Career
 *   abandon and an `ow-` record's release, settled once), *Close* does nothing.
 * - **One prompt for the next step** (`prompts`, an `InteractableProvider`) at the nearest surveyed hulk within the M3
 *   `portRangeM`: vent the coolant (`ventHulk`, its `hullDamage` applied to the ship), fight the squat
 *   (`hulkSquatWaves` spawned on the raider team, `markHulkSquatFought` once every group is dead; while it is out the
 *   same prompt offers the fence fee, `payHulkFence`, which despawns it), start the kit (`FleetService.openHulkClaim`:
 *   an `ow-` record is opened in the worker first), deliver (`deliver`, else one hold component as a stand-in,
 *   `standIn`) and claim (`FleetService.claimHulk` at the hulk's position, ferrying to the last docked station).
 *   Deliveries and the claim need the ship below `portMaxSpeed`.
 * - **PF-2, orphaned claims:** an open claim whose hulk can never come back (`kit.ts orphanedHulkClaims`: a dev hulk
 *   gone with its system or a load, a seeded key the placement no longer has) is abandoned through the same
 *   `FleetService.closeHulkClaim` as the card's button, one at a time, said on comms (`text.live.claimLost`). It is
 *   checked in `step` whenever the Career snapshot or the sources change (two compares per tick otherwise), so a
 *   claim loaded with a save is caught once the fleet service is up. A refused close is logged and retried on the
 *   next change.
 * - **Writes:** every Career step runs first on the live Career; only a success is written (`apply`, re-run there on
 *   the Career it holds), so a refusal writes nothing. The fleet calls do the Career and the worker together
 *   (CH-7s): a claim the worker refuses leaves the kit complete.
 *
 * `nearest` and `step` allocate nothing (D-014): the hulk list and each step's static block are rebuilt only when
 * the Career snapshot or the sources change (event rate), positions are read in place. Scans, activations and the
 * squat's spawns are rare events.
 */
import { GOOD_NAMES } from '../career/ops'
import type { GoodId } from '../career/parts'
import type { Career, HulkSite } from '../career/types'
import { applyDamage, createDamageResult } from '../combat/damage'
import { logger } from '../core/logger'
import type { Vec3T } from '../galaxy/layout/types'
import type { RegionId } from '../galaxy/types'
import type { FleetOutcome, HulkClaimAt } from '../fleet-link/service'
import { loadRelinkConfig, type RelinkConfig } from '../relink/config'
import type { InteractableProvider, InteractionKind, InteractionPrompt, ReachCore } from '../reach/runtime/types'
import type { ReachCommandResult } from '../reach/types'
import type { ModalSpec } from '../reach/ui/modal'
import { RAIDER_TEAM, type Sim } from '../sim'
import { hash32, hashString } from '../space/hash'
import { loadReactivationTuning, type ReactivationTuning } from '../story-link/reactivation/defence'
import { COOLANT, type CoolantConfig } from '../story-link/reactivation/restore'
import { reactivationText, type ReactivationText } from '../story-link/reactivation/survey'
import { fillTokens } from '../story-link/world'
import type { CmdOutcome } from '../ui/contracts'
import { HULK_CARD_BUTTON, hulkCardModal, hulkScreenView } from '../ui/hulks/models'
import { Life, Pos64, Vel } from '../world/components'
import { hulksConfig, type HulksConfig } from './data'
import { claimMaxOpen, deliver, kitDone, openHulkCount, orphanedHulkClaims, standIn, type HulkDeliverResult, type HulkKitRefusal } from './kit'
import { hulkSquatWaves, markHulkSquatFought, payHulkFence, siteClearRefusal, ventHulk, type HulkSecureRefusal, type HulkSecureResult } from './secure'
import { hulkHazards, hulkSurvey, type HulkHazards, type HulkRef } from './survey'

export type HulkPromptKind = Extract<InteractionKind, 'hulkVent' | 'hulkFence' | 'hulkFight' | 'hulkStart' | 'hulkDeliver' | 'hulkClaim'>

/** A hulk the feature knows in the current system: a seeded spot or an off-screen record. */
export interface HulkSource {
  readonly key: string
  readonly hull: string
  readonly seed: number
  /** System frame, float64 metres. */
  readonly position: Readonly<Vec3T>
}

/** A surveyed hulk in the current system and its next step. */
export interface LiveHulk {
  readonly ref: HulkRef
  readonly hazards: HulkHazards
  readonly x: number
  readonly y: number
  readonly z: number
  readonly action: HulkPromptKind
  /** What blocks the step regardless of the ship's speed (null = nothing). */
  readonly blocked: string | null
  /** The step obeys `portMaxSpeed` (deliveries and the claim). */
  readonly slow: boolean
}

/** The fleet service's hulk calls (CH-7s) the loop uses. */
export interface HulkFleetPort {
  openHulkClaim(key: string): Promise<FleetOutcome>
  closeHulkClaim(key: string): Promise<FleetOutcome>
  claimHulk(key: string, at: HulkClaimAt): Promise<FleetOutcome>
}

export interface HulkLiveDeps {
  career(): Career | null
  /** The Career write hook: a pure change; a throw refuses it (`CareerAccess.apply`). */
  apply(name: string, cmd: (career: Career) => Career): CmdOutcome
  /** The fleet service (null while it isn't up: the kit start and the claim wait). */
  fleet(): HulkFleetPort | null
  /**
   * Shows the survey card (CH-12b's `hulkCardModal`, or the Mk I refusal with only *Close*) and resolves with the
   * chosen button id (`HULK_CARD_BUTTON`); a card that could not be shown resolves `'close'`.
   */
  showCard(card: ModalSpec): Promise<string>
  /** A one-line player-facing message (a step's result or refusal). */
  say(text: string): void
  /** A hulk was claimed (the finder drops it from the nav points). */
  claimed?(key: string): void
  /**
   * CH-12c: the strategic station a Career station id plays (`lastDockedStation` is an M2 id such as `hub` or
   * `sys_01_st7`; the worker's `registerClaimedShip` takes the site id `sys-01-st7`), with its name; null when no
   * site plays it. Absent: the id is used as it is (tests with a fake worker).
   */
  dockStation?(careerStationId: string): { readonly id: string; readonly name: string } | null
  /**
   * CH-12g: the home station's site (the Hub), where a claim goes when the last dock resolves to no site or the worker
   * refuses it `unknownStation` (one retry, said on comms with `text.live.claimedHome`). Absent: no fallback.
   */
  homeStation?(): { readonly id: string; readonly name: string } | null
  relink?: RelinkConfig
  tuning?: ReactivationTuning
  coolant?: CoolantConfig
  cfg?: HulksConfig
  text?: ReactivationText
}

export interface HulkLive {
  /**
   * The seeded hulks of `systemId` (on entry; finished ones are left out when listing). `placed` false: the spots
   * could not be placed (the story overlay failed), so no seeded claim of the system is judged orphaned (PF-2).
   */
  setSeeded(systemId: string, region: RegionId, spots: readonly HulkSource[], placed?: boolean): void
  /** The manifest's hulk records of `systemId` (`ow-` keys). */
  setOffscreen(systemId: string, records: readonly HulkSource[]): void
  /**
   * CH-12e: a dev-spawned hulk (`hk:<sys>:dev<n>`, CH-12d) in the current system at `position` (system frame). Its
   * seed is `devHulkSeed(key, region)`. Kept until the system is left (a dev hulk lives only that long).
   */
  addDevHulk(key: string, hull: string, position: Readonly<Vec3T>): void
  /** A completed wreck scan of the hulk `key` with Scanner Mk `scannerTier`: the survey card. */
  scanned(key: string, scannerTier: number): void
  readonly prompts: InteractableProvider
  /** Per tick: the squat fight, and PF-2's orphaned-claim check when the Career or the sources changed. Allocation-free otherwise. */
  step(core: ReachCore, sim: Sim): void
  /** The system is being left: a squat fight breaks off. */
  leave(core: ReachCore): void
  /** The current system's surveyed hulks and their next steps (tests, the debug API). */
  readonly hulks: readonly LiveHulk[]
  /** The squat that is out (`''` = none). */
  readonly squatKey: string
}

/** Runs once against the live Career; the result carries the new Career on success. */
type Step = { ok: true; career: Career; message: string; hullDamage?: number } | { ok: false; message: string }

const NONE: readonly HulkSource[] = []

/** CH-12e: a dev hulk's seed, a hash of its key and the system's region (CH-4 rolls its flaws from it). */
export function devHulkSeed(key: string, region: RegionId): number {
  return hash32(hashString(key), hashString(region))
}

export function createHulkLive(deps: HulkLiveDeps): HulkLive {
  const cfg = deps.cfg ?? hulksConfig()
  const relink = deps.relink ?? loadRelinkConfig()
  const tuning = deps.tuning ?? loadReactivationTuning()
  const coolant = deps.coolant ?? COOLANT
  const text = deps.text ?? reactivationText()
  const t = cfg.text
  const hulks: LiveHulk[] = []
  const damage = createDamageResult()

  let systemId = ''
  let region: RegionId = 'gate'
  let seeded: readonly HulkSource[] = NONE
  let offscreen: readonly HulkSource[] = NONE
  let offscreenSystem = ''
  /** CH-12e: dev-spawned hulks of the current system (cleared on every system entry). */
  let dev: HulkSource[] = []
  let sourcesVersion = 0
  let seenVersion = -1
  let seenCareer: Career | null = null
  /** A fleet call in flight (the kit start, the claim): its key; a second press waits. */
  let pending = ''
  /** PF-2: the seeded spots of the current system were placed (false: no seeded claim is judged). */
  let seededPlaced = true
  /** PF-2: the Career snapshot and sources the orphan check last saw. */
  let sweptCareer: Career | null = null
  let sweptVersion = -1

  const squat = { key: '', systemId: '', center: [0, 0, 0] as Vec3T, groups: [] as number[] }

  // -------------------------------------------------------------------------------------------
  // Text
  // -------------------------------------------------------------------------------------------

  function kitRefusalText(reason: HulkKitRefusal): string {
    switch (reason) {
      case 'noSite':
      case 'notSurveyed':
        return t.refusals.noSite
      case 'notEligible':
        return t.refusals.notEligible
      case 'tooManyOpen':
        return fillTokens(t.live.tooManyOpen, { max: String(claimMaxOpen()) })
      case 'notOpen':
        return t.prompts.start
      case 'hazard':
      case 'squatters':
        return text.refusals[reason]
      case 'complete':
        return t.live.kitComplete
      default:
        return t.live.nothing
    }
  }

  function secureRefusalText(reason: HulkSecureRefusal): string {
    switch (reason) {
      case 'noSite':
        return t.refusals.noSite
      case 'noHazard':
        return text.messages.vented
      case 'noSquat':
        return text.messages.fought
      default:
        return text.refusals[reason]
    }
  }

  const KIT_REFUSALS: ReadonlySet<string> = new Set<HulkKitRefusal>(['noSite', 'notSurveyed', 'notEligible', 'tooManyOpen', 'notOpen', 'hazard', 'squatters', 'complete'])

  /** A fleet refusal: the kit's own reasons read as the kit's lines, a worker code as "did not go through". */
  function fleetRefusalText(error: string): string {
    return KIT_REFUSALS.has(error) ? kitRefusalText(error as HulkKitRefusal) : fillTokens(t.live.claimRefused, { reason: error })
  }

  function deliveredText(r: Extract<HulkDeliverResult, { ok: true }>): string {
    const parts: string[] = []
    for (const [g, n] of Object.entries(r.goods) as [GoodId, number | undefined][]) if (n !== undefined && n > 0) parts.push(`${n} ${GOOD_NAMES[g]}`)
    const line = fillTokens(t.live.delivered, { what: parts.join(', ') })
    return r.done ? `${line} ${t.live.kitComplete}` : line
  }

  // -------------------------------------------------------------------------------------------
  // The hulk list (event rate)
  // -------------------------------------------------------------------------------------------

  function refOf(src: HulkSource): HulkRef {
    return { key: src.key, hull: src.hull, seed: src.seed, region }
  }

  function findSource(key: string): HulkSource | null {
    for (const s of seeded) if (s.key === key) return s
    for (const s of dev) if (s.key === key) return s
    if (offscreenSystem === systemId) for (const s of offscreen) if (s.key === key) return s
    return null
  }

  /** A delivery dry run: goods first, else the first hold component that stands in. */
  function tryDeliver(career: Career, ref: HulkRef): HulkDeliverResult {
    const r = deliver(career, ref, { cfg })
    if (r.ok || r.reason !== 'nothingToDeliver') return r
    for (const part of career.cargo.components) {
      const s = standIn(career, ref, part.id, { cfg })
      if (s.ok) return s
    }
    return r
  }

  /** The next step at a surveyed, unfinished hulk (null: nothing to prompt). */
  function stepOf(career: Career, ref: HulkRef, hazards: HulkHazards, site: HulkSite): Pick<LiveHulk, 'action' | 'blocked' | 'slow'> | null {
    const clear = siteClearRefusal(hazards, site)
    if (clear === 'hazard') return { action: 'hulkVent', blocked: null, slow: false }
    if (clear === 'squatters') return { action: 'hulkFight', blocked: null, slow: false }
    if (site.open !== true) {
      const full = openHulkCount(career) >= claimMaxOpen()
      return { action: 'hulkStart', blocked: full ? kitRefusalText('tooManyOpen') : null, slow: false }
    }
    if (!kitDone(career, ref.key, cfg)) {
      const d = tryDeliver(career, ref)
      return { action: 'hulkDeliver', blocked: d.ok ? null : kitRefusalText(d.reason), slow: true }
    }
    return { action: 'hulkClaim', blocked: null, slow: true }
  }

  function addHulk(career: Career, src: HulkSource): void {
    const state = career.hulks
    if (state === undefined || state.done.includes(src.key)) return
    const site = state.sites[src.key]
    if (site === undefined || site.surveyed === undefined || site.claimedAt !== undefined) return
    const ref = refOf(src)
    const hazards = hulkHazards(ref, cfg)
    const step = stepOf(career, ref, hazards, site)
    if (step === null) return
    hulks.push({ ref, hazards, x: src.position[0], y: src.position[1], z: src.position[2], ...step })
  }

  function rebuild(career: Career): void {
    hulks.length = 0
    for (const s of seeded) addHulk(career, s)
    for (const s of dev) addHulk(career, s)
    if (offscreenSystem === systemId) for (const s of offscreen) addHulk(career, s)
  }

  /** Rebuilds the list when the Career snapshot or the sources changed. Allocation-free otherwise. */
  function refresh(): void {
    const career = deps.career()
    if (career === seenCareer && sourcesVersion === seenVersion) return
    seenCareer = career
    seenVersion = sourcesVersion
    if (career === null) hulks.length = 0
    else rebuild(career)
  }

  const invalidate = (): void => {
    seenCareer = null
  }

  // -------------------------------------------------------------------------------------------
  // Steps
  // -------------------------------------------------------------------------------------------

  function livePlayer(core: ReachCore, sim: Sim): number {
    const p = sim.playerEid
    const layout = core.session.layout
    return p >= 0 && Life.alive[p] === 1 && core.session.ready && layout !== null && layout.systemId === systemId ? p : -1
  }

  /** Runs `fn` on the live Career first; only a success is written (re-run on the hook's Career). Says the result. */
  function run(name: string, fn: (c: Career) => Step): Step | null {
    const career = deps.career()
    if (career === null) return null
    const first = fn(career)
    if (!first.ok) {
      deps.say(first.message)
      return first
    }
    // A box: the hook re-runs the step on the Career it holds (TS does not track a closure's writes to a let).
    const box: { r: Step } = { r: first }
    const out = deps.apply(name, (c) => {
      const again = fn(c)
      box.r = again
      if (!again.ok) throw new Error(again.message)
      return again.career
    })
    const applied = box.r
    if (!out.ok) {
      const message = applied.ok ? out.error.message : applied.message
      deps.say(message)
      invalidate()
      return { ok: false, message }
    }
    deps.say(applied.message)
    invalidate()
    return applied
  }

  const secured = (r: HulkSecureResult): Step => (r.ok ? { ok: true, career: r.career, message: r.message, hullDamage: r.hullDamage } : { ok: false, message: secureRefusalText(r.reason) })

  const result = (r: Step | null): ReachCommandResult =>
    r === null ? { ok: false, error: 'unavailable', message: '' } : r.ok ? { ok: true, message: r.message } : { ok: false, error: 'invalid', message: r.message }

  function refuse(error: 'invalid' | 'busy' | 'unavailable', message: string): ReachCommandResult {
    if (message !== '') deps.say(message)
    return { ok: false, error, message }
  }

  // --- the squat fight ---------------------------------------------------------------------

  function clearSquat(core: ReachCore | null): void {
    if (core !== null) for (const g of squat.groups) core.session.despawnGroup(g)
    squat.key = ''
    squat.systemId = ''
    squat.groups = []
  }

  function fight(h: LiveHulk, core: ReachCore, sim: Sim): ReachCommandResult {
    if (squat.key !== '' || core.relink.phase !== 'idle') return refuse('busy', t.live.busy)
    const layout = core.session.layout
    if (layout === null) return refuse('unavailable', '')
    const career = deps.career()
    if (career === null) return refuse('unavailable', '')
    const check = markHulkSquatFought(career, h.ref, cfg, text)
    if (!check.ok) return refuse('invalid', secureRefusalText(check.reason))
    const p = sim.playerEid
    const dx = Pos64.x[p] - h.x
    const dy = Pos64.y[p] - h.y
    const dz = Pos64.z[p] - h.z
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
    const facing: Vec3T = len > 1e-6 ? [dx / len, dy / len, dz / len] : [0, 0, 1]
    const center: Vec3T = [h.x, h.y, h.z]
    const s = tuning.squat
    const groups: number[] = []
    for (const w of hulkSquatWaves(layout.region, relink)) {
      groups.push(core.session.spawnGroup({ ships: w.ships, center, facing, radiusM: s.spawnRadiusM, team: RAIDER_TEAM, kind: w.kind, despawnM: s.despawnM }))
    }
    squat.key = h.ref.key
    squat.systemId = layout.systemId
    squat.center = center
    squat.groups = groups
    logger.info(`hulks: the squat at ${h.ref.key} is out (${groups.length} groups)`)
    const message = fillTokens(t.live.squatOut, { fee: String(h.hazards.fenceFee) })
    deps.say(message)
    return { ok: true, message }
  }

  /** Every squat ship is dead: the squat is marked fought (rare: allocates). */
  function squatDown(): void {
    const key = squat.key
    clearSquat(null)
    const src = findSource(key)
    if (src === null) return
    const ref = refOf(src)
    run('hulk squat fought', (c) => secured(markHulkSquatFought(c, ref, cfg, text)))
  }

  // --- the fleet calls (async; one at a time) ----------------------------------------------

  function startKit(key: string): ReachCommandResult {
    const fleet = deps.fleet()
    if (fleet === null) return refuse('unavailable', t.live.noFleet)
    if (pending !== '') return refuse('busy', '')
    pending = key
    void fleet
      .openHulkClaim(key)
      .then((r) => deps.say(r.ok ? t.live.kitStarted : fleetRefusalText(r.error)))
      .catch((err: unknown) => logger.error(`hulks: openHulkClaim ${key} threw`, err))
      .finally(() => {
        pending = ''
        invalidate()
      })
    return { ok: true, message: '' }
  }

  /** CH-12e: the card's *Abandon the claim* (the fleet service abandons on the Career and releases an `ow-` record). */
  function abandonKit(key: string): ReachCommandResult {
    const fleet = deps.fleet()
    if (fleet === null) return refuse('unavailable', t.live.noFleet)
    if (pending !== '') return refuse('busy', '')
    pending = key
    void fleet
      .closeHulkClaim(key)
      .then((r) => deps.say(r.ok ? t.live.kitAbandoned : KIT_REFUSALS.has(r.error) ? kitRefusalText(r.error as HulkKitRefusal) : fillTokens(t.live.abandonRefused, { reason: r.error })))
      .catch((err: unknown) => logger.error(`hulks: closeHulkClaim ${key} threw`, err))
      .finally(() => {
        pending = ''
        invalidate()
      })
    return { ok: true, message: '' }
  }

  /** CH-12e: the survey card's answer, through the same fleet calls as the prompts (one settlement each). */
  function answer(key: string, id: string): void {
    if (id === HULK_CARD_BUTTON.startKit) startKit(key)
    else if (id === HULK_CARD_BUTTON.abandon) abandonKit(key)
  }

  function show(card: ModalSpec, key: string | null): void {
    void deps
      .showCard(card)
      .then((id) => {
        if (key !== null) answer(key, id)
      })
      .catch((err: unknown) => logger.error('hulks: the survey card failed', err))
  }

  function claim(h: LiveHulk): ReachCommandResult {
    const fleet = deps.fleet()
    if (fleet === null) return refuse('unavailable', t.live.noFleet)
    const career = deps.career()
    if (career === null) return refuse('unavailable', t.live.noFleet)
    // R3-2: every refusal of the claim reaches comms (a silent press reads as "nothing happened").
    if (pending !== '') return refuse('busy', t.live.claimPending)
    const key = h.ref.key
    const resolved = deps.dockStation === undefined ? { id: career.lastDockedStation, name: career.lastDockedStation } : deps.dockStation(career.lastDockedStation)
    // CH-12g: a dock no site plays (or one the worker does not know) sends the ship to the home station instead.
    const home = deps.homeStation?.() ?? null
    const dock = resolved ?? home
    if (dock === null) return refuse('invalid', fillTokens(t.live.claimRefused, { reason: 'unknownStation' }))
    const pos: [number, number, number] = [h.x, h.y, h.z]
    const atOf = (d: { readonly id: string }): HulkClaimAt => ({ systemId, pos, dockAt: d.id })
    const hull = t.hulls[h.ref.hull] ?? h.ref.hull
    pending = key
    // R3-2: an order the fleet link doesn't answer says so; it stays pending (a second order could register twice).
    const slow = setTimeout(() => deps.say(t.live.claimSlow), cfg.claim.answerTimeoutS * 1000)
    void (async () => {
      let to = dock
      let r = await fleet.claimHulk(key, atOf(to))
      // One retry at home: the worker refused the dock before registering anything, so at most one ship registers.
      if (!r.ok && r.error === 'unknownStation' && home !== null && to.id !== home.id) {
        to = home
        r = await fleet.claimHulk(key, atOf(to))
      }
      return { r, to }
    })()
      .then(({ r, to }) => {
        if (!r.ok) {
          deps.say(fleetRefusalText(r.error))
          return
        }
        const line = to === home && to !== resolved ? t.live.claimedHome : t.live.claimed
        deps.say(fillTokens(line, { hull, station: to.name }))
        deps.claimed?.(key)
      })
      .catch((err: unknown) => {
        logger.error(`hulks: claimHulk ${key} threw`, err)
        deps.say(fillTokens(t.live.claimRefused, { reason: err instanceof Error ? err.message : String(err) }))
      })
      .finally(() => {
        clearTimeout(slow)
        pending = ''
        invalidate()
      })
    return { ok: true, message: '' }
  }

  // --- PF-2: orphaned claims ---------------------------------------------------------------

  /** Abandons every open claim whose hulk can never come back (rare: allocates). Waits while a fleet call runs. */
  function sweepOrphans(career: Career): void {
    const fleet = deps.fleet()
    if (fleet === null || systemId === '' || pending !== '') return
    sweptCareer = career
    sweptVersion = sourcesVersion
    const live = new Set<string>()
    for (const s of seeded) live.add(s.key)
    for (const s of dev) live.add(s.key)
    const keys = orphanedHulkClaims(career, systemId, live, seededPlaced)
    if (keys.length === 0) return
    pending = keys[0]
    void (async () => {
      for (const key of keys) {
        const hullId = deps.career()?.hulks?.sites[key]?.hull ?? ''
        const r = await fleet.closeHulkClaim(key)
        if (r.ok) {
          logger.info(`hulks: the claim on ${key} is closed (the hulk can't come back)`)
          deps.say(fillTokens(t.live.claimLost, { hull: t.hulls[hullId] ?? hullId }))
        } else logger.warn(`hulks: closing the orphaned claim ${key} was refused (${r.error})`)
      }
    })()
      .catch((err: unknown) => logger.error('hulks: closing the orphaned claims threw', err))
      .finally(() => {
        // A refused close is retried on the next Career or source change (a success changes the Career).
        pending = ''
        invalidate()
      })
  }

  // -------------------------------------------------------------------------------------------
  // The prompts
  // -------------------------------------------------------------------------------------------

  const prompts: InteractableProvider = {
    nearest(core: ReachCore, sim: Sim, out: InteractionPrompt): boolean {
      const p = livePlayer(core, sim)
      if (p < 0 || core.travel.mode !== 'normal') return false
      refresh()
      if (hulks.length === 0) return false
      const px = Pos64.x[p]
      const py = Pos64.y[p]
      const pz = Pos64.z[p]
      let best = -1
      let bestD2 = relink.portRangeM * relink.portRangeM
      for (let i = 0; i < hulks.length; i++) {
        const h = hulks[i]
        const dx = h.x - px
        const dy = h.y - py
        const dz = h.z - pz
        const d2 = dx * dx + dy * dy + dz * dz
        if (d2 <= bestD2) {
          best = i
          bestD2 = d2
        }
      }
      if (best < 0) return false
      const h = hulks[best]
      const vx = Vel.x[p]
      const vy = Vel.y[p]
      const vz = Vel.z[p]
      const fast = vx * vx + vy * vy + vz * vz > relink.portMaxSpeed * relink.portMaxSpeed
      out.kind = h.action
      out.index = best
      out.distance = Math.sqrt(bestD2)
      if (h.action === 'hulkFight' && squat.key === h.ref.key) {
        // The squat is out: the same prompt offers the fence fee (paying despawns it).
        const c = deps.career()
        out.kind = 'hulkFence'
        out.blocked = c !== null && c.wallet < h.hazards.fenceFee ? text.refusals.funds : null
      } else if (h.blocked !== null) out.blocked = h.blocked
      else if (h.action === 'hulkFight' && (core.relink.phase !== 'idle' || squat.key !== '')) out.blocked = t.live.busy
      else if (pending !== '' && (h.action === 'hulkStart' || h.action === 'hulkClaim')) out.blocked = t.live.busy
      else out.blocked = h.slow && fast ? t.live.speed : null
      return true
    },
    activate(core: ReachCore, sim: Sim, prompt: InteractionPrompt): ReachCommandResult {
      const h = prompt.index >= 0 && prompt.index < hulks.length ? hulks[prompt.index] : undefined
      const p = livePlayer(core, sim)
      if (h === undefined || p < 0) return { ok: false, error: 'invalid', message: '' }
      const fence = h.action === 'hulkFight' && prompt.kind === 'hulkFence' && squat.key === h.ref.key
      if (h.action !== prompt.kind && !fence) return { ok: false, error: 'invalid', message: '' }
      const dx = Pos64.x[p] - h.x
      const dy = Pos64.y[p] - h.y
      const dz = Pos64.z[p] - h.z
      const dist2 = dx * dx + dy * dy + dz * dz
      const range2 = relink.portRangeM * relink.portRangeM
      if (dist2 > range2) return { ok: false, error: 'invalid', message: '' }
      const speed2 = Vel.x[p] ** 2 + Vel.y[p] ** 2 + Vel.z[p] ** 2
      if (h.slow && speed2 > relink.portMaxSpeed * relink.portMaxSpeed) return refuse('invalid', t.live.speed)
      const ref = h.ref
      if (fence) {
        const r = run('hulk fence', (c) => secured(payHulkFence(c, ref, cfg, text)))
        if (r?.ok === true) clearSquat(core)
        return result(r)
      }
      switch (h.action) {
        case 'hulkVent': {
          const r = run('hulk vent', (c) => secured(ventHulk(c, ref, dist2, coolant, cfg, text)))
          if (r?.ok === true && (r.hullDamage ?? 0) > 0) applyDamage(p, r.hullDamage ?? 0, 1, 1, sim.time, damage)
          return result(r)
        }
        case 'hulkFight':
          return fight(h, core, sim)
        case 'hulkFence':
          return result(run('hulk fence', (c) => secured(payHulkFence(c, ref, cfg, text))))
        case 'hulkStart':
          return startKit(ref.key)
        case 'hulkDeliver':
          return result(
            run('hulk kit delivery', (c) => {
              const d = tryDeliver(c, ref)
              return d.ok ? { ok: true, career: d.career, message: deliveredText(d) } : { ok: false, message: kitRefusalText(d.reason) }
            }),
          )
        case 'hulkClaim':
          return claim(h)
      }
    },
  }

  return {
    hulks,
    get squatKey(): string {
      return squat.key
    },
    prompts,
    setSeeded(id, reg, spots, placed = true) {
      if (id !== systemId) offscreen = NONE
      // A dev hulk lives only until its system is left: every entry (re-entering the same system too) drops them.
      dev = []
      systemId = id
      region = reg
      seeded = spots
      seededPlaced = placed
      sourcesVersion++
    },
    setOffscreen(id, records) {
      offscreenSystem = id
      offscreen = records
      sourcesVersion++
    },
    addDevHulk(key, hull, position) {
      if (findSource(key) !== null) return
      if (!key.startsWith(`hk:${systemId}:`)) {
        logger.warn(`hulks: dev hulk ${key} is not in the current system (${systemId})`)
        return
      }
      dev = [...dev, { key, hull, seed: devHulkSeed(key, region), position: [position[0], position[1], position[2]] }]
      sourcesVersion++
    },
    scanned(key, scannerTier) {
      const src = findSource(key)
      const career = deps.career()
      if (src === null || career === null) {
        logger.debug(`hulks: a scanned hulk ${key} is not in the current system's list`)
        return
      }
      const ref = refOf(src)
      const first = hulkSurvey(ref, career, scannerTier, cfg)
      if (!first.ok) {
        if (first.reason === 'scanner') show({ title: fillTokens(t.title, { hull: t.hulls[src.hull] ?? src.hull }), body: first.text, buttons: [{ id: HULK_CARD_BUTTON.close, label: t.close, primary: true }] }, null)
        else deps.say(first.text)
        return
      }
      let after = career
      if (first.career !== career) {
        const out = deps.apply('hulk survey', (c) => {
          const again = hulkSurvey(ref, c, scannerTier, cfg)
          if (!again.ok) throw new Error(again.text)
          return again.career
        })
        if (!out.ok) {
          deps.say(out.error.message)
          return
        }
        after = deps.career() ?? first.career
        invalidate()
      }
      show(hulkCardModal(hulkScreenView(first.card, after, cfg)), key)
    },
    step(core, sim) {
      const career = deps.career()
      if (career !== null && (career !== sweptCareer || sourcesVersion !== sweptVersion)) sweepOrphans(career)
      if (squat.key === '') return
      const layout = core.session.layout
      const p = sim.playerEid
      let broken = layout === null || layout.systemId !== squat.systemId || p < 0 || Life.alive[p] !== 1
      if (!broken) {
        const at = squat.center
        const dx = Pos64.x[p] - at[0]
        const dy = Pos64.y[p] - at[1]
        const dz = Pos64.z[p] - at[2]
        const leave = tuning.squat.leaveAbortM
        broken = dx * dx + dy * dy + dz * dz > leave * leave
      }
      if (broken) {
        clearSquat(core)
        deps.say(t.live.squatLeft)
        return
      }
      const groups = squat.groups
      let alive = 0
      for (let i = 0; i < groups.length; i++) alive += core.session.groupAlive(groups[i])
      if (alive === 0) squatDown()
    },
    leave(core) {
      if (squat.key !== '') clearSquat(core)
    },
  }
}
